import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  notification,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import * as api from 'util/api';
import { formatCacheAge, formatRowCount } from 'util/formatCacheAge';

const { Text } = Typography;

const POLL_MS = 2000;
const SHOPIFY_STORES = ['GPNZ', 'GPAU'];

function errorMessage(err, fallback) {
  return err?.response?.data?.error
    || err?.response?.data?.message
    || err?.message
    || fallback;
}

function shopifyStoreStats(overview, store) {
  const stores = overview?.shopify?.stores;
  if (!Array.isArray(stores)) return { rowCount: null, asOf: null };
  return stores.find((row) => row.store === store) || { rowCount: null, asOf: null };
}

function PlatformCard({
  title,
  extra,
  children,
  onRefresh,
  refreshLabel,
  refreshing,
  disabled,
}) {
  return (
    <Card
      size="small"
      title={title}
      extra={extra}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {children}
        <Button
          type="primary"
          icon={<SyncOutlined />}
          onClick={onRefresh}
          loading={refreshing}
          disabled={disabled || refreshing}
        >
          {refreshing ? 'Refreshing…' : refreshLabel}
        </Button>
      </Space>
    </Card>
  );
}

PlatformCard.propTypes = {
  title: PropTypes.string.isRequired,
  extra: PropTypes.node,
  children: PropTypes.node.isRequired,
  onRefresh: PropTypes.func.isRequired,
  refreshLabel: PropTypes.string.isRequired,
  refreshing: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
};

PlatformCard.defaultProps = {
  extra: null,
  disabled: false,
};

const CacheOverview = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState({
    shopify: false,
    cin7: false,
    kounta: false,
  });

  const fetchOverview = useCallback(async () => {
    try {
      const data = await api.getProductCacheOverview();
      setOverview(data);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err, 'Failed to load cache overview'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const cin7Job = overview?.jobs?.cin7;
  const kountaJob = overview?.jobs?.kountaApi;
  const cin7Running = cin7Job?.status === 'running';
  const kountaRunning = kountaJob?.status === 'running';

  useEffect(() => {
    if (!cin7Running && !kountaRunning) {
      return undefined;
    }
    const id = setInterval(fetchOverview, POLL_MS);
    return () => clearInterval(id);
  }, [cin7Running, kountaRunning, fetchOverview]);

  const handleShopifyRefresh = async () => {
    setRefreshing((prev) => ({ ...prev, shopify: true }));
    try {
      const nz = await api.refreshProductCacheShopify(SHOPIFY_STORES[0]);
      let au = null;
      let auError = null;
      try {
        au = await api.refreshProductCacheShopify(SHOPIFY_STORES[1]);
      } catch (err) {
        auError = err;
      }
      await fetchOverview();
      const line = (store, result) => (result
        ? `${store}: ${result.variants ?? 0} variant(s)`
        : null);
      notification[auError ? 'warning' : 'success']({
        message: 'Shopify price cache refreshed',
        description: [
          line(SHOPIFY_STORES[0], nz),
          line(SHOPIFY_STORES[1], au),
          auError
            ? `${SHOPIFY_STORES[1]} failed: ${errorMessage(auError, 'unknown error')}`
            : null,
        ].filter(Boolean).join(' · '),
      });
    } catch (err) {
      notification.error({
        message: errorMessage(err, 'Shopify refresh failed'),
      });
    } finally {
      setRefreshing((prev) => ({ ...prev, shopify: false }));
    }
  };

  const handleCin7Refresh = async () => {
    setRefreshing((prev) => ({ ...prev, cin7: true }));
    try {
      const result = await api.syncCin7Products(false);
      if (result?.status === 409) {
        notification.warning({ message: 'A Cin7 sync is already in progress.' });
      } else if (result?.status >= 400) {
        notification.error({
          message: result?.data?.error
            || result?.data?.message
            || `Cin7 sync failed (${result.status})`,
        });
      }
      await fetchOverview();
    } catch (err) {
      notification.error({
        message: errorMessage(err, 'Failed to start Cin7 sync'),
      });
    } finally {
      setRefreshing((prev) => ({ ...prev, cin7: false }));
    }
  };

  const handleKountaRefresh = async () => {
    setRefreshing((prev) => ({ ...prev, kounta: true }));
    try {
      const result = await api.syncKountaApiProducts();
      if (result?.status === 409) {
        notification.warning({ message: 'A Kounta API sync is already in progress.' });
      } else if (result?.status >= 400) {
        notification.error({
          message: result?.data?.error
            || result?.data?.message
            || `Kounta sync failed (${result.status})`,
        });
      }
      await fetchOverview();
    } catch (err) {
      notification.error({
        message: errorMessage(err, 'Failed to start Kounta sync'),
      });
    } finally {
      setRefreshing((prev) => ({ ...prev, kounta: false }));
    }
  };

  if (loading && !overview) {
    return <Spin />;
  }

  const gpnz = shopifyStoreStats(overview, 'GPNZ');
  const gpau = shopifyStoreStats(overview, 'GPAU');
  const cin7Pages = cin7Job?.totalPages || 0;
  const cin7Page = cin7Job?.page || 0;
  const cin7Progress = cin7Pages > 0
    ? Math.min(100, Math.round((cin7Page / cin7Pages) * 100))
    : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {loadError ? (
        <Alert type="error" message={loadError} showIcon />
      ) : null}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <PlatformCard
            title="Shopify"
            extra={<Text type="secondary">Price cache</Text>}
            onRefresh={handleShopifyRefresh}
            refreshLabel="Refresh"
            refreshing={refreshing.shopify}
          >
            <Text>
              GPNZ:
              {' '}
              {formatRowCount(gpnz.rowCount)}
              {' '}
              variants
            </Text>
            <Text>
              GPAU:
              {' '}
              {formatRowCount(gpau.rowCount)}
              {' '}
              variants
            </Text>
            <Text type="secondary">{formatCacheAge(overview?.shopify?.asOf)}</Text>
          </PlatformCard>
        </Col>
        <Col xs={24} md={8}>
          <PlatformCard
            title="Cin7"
            extra={<Text type="secondary">Product cache</Text>}
            onRefresh={handleCin7Refresh}
            refreshLabel={cin7Running ? 'Syncing…' : 'Refresh'}
            refreshing={refreshing.cin7}
            disabled={cin7Running}
          >
            <Text>
              {formatRowCount(overview?.cin7?.rowCount)}
              {' '}
              products
            </Text>
            <Text type="secondary">{formatCacheAge(overview?.cin7?.asOf)}</Text>
            {cin7Running ? (
              <div>
                {cin7Job?.mode ? (
                  <Tag color={cin7Job.mode === 'incremental' ? 'blue' : 'orange'}>
                    {cin7Job.mode === 'incremental' ? 'Incremental' : 'Full'}
                  </Tag>
                ) : null}
                <Text type="secondary">
                  Page
                  {' '}
                  {cin7Page}
                  {' '}
                  (
                  {cin7Job?.syncedCount || 0}
                  {' '}
                  products)
                </Text>
                <Progress percent={cin7Progress} status="active" size="small" />
              </div>
            ) : null}
            {cin7Job?.status === 'failed' && cin7Job?.error ? (
              <Alert type="error" message={String(cin7Job.error)} showIcon />
            ) : null}
          </PlatformCard>
        </Col>
        <Col xs={24} md={8}>
          <PlatformCard
            title="Kounta"
            extra={<Text type="secondary">API + CSV</Text>}
            onRefresh={handleKountaRefresh}
            refreshLabel={kountaRunning ? 'Syncing…' : 'Refresh'}
            refreshing={refreshing.kounta}
            disabled={kountaRunning}
          >
            <Text>
              API:
              {' '}
              {formatRowCount(overview?.kountaApi?.rowCount)}
              {' '}
              products
            </Text>
            <Text type="secondary">
              API
              {' '}
              {formatCacheAge(overview?.kountaApi?.asOf)}
            </Text>
            <Text>
              CSV:
              {' '}
              {formatRowCount(overview?.kountaCsv?.rowCount)}
              {' '}
              products
            </Text>
            <Text type="secondary">
              CSV
              {' '}
              {formatCacheAge(overview?.kountaCsv?.asOf)}
            </Text>
            {kountaRunning ? (
              <Text type="secondary">
                {kountaJob?.pagesFetched || 0}
                {' '}
                pages ·
                {' '}
                {kountaJob?.syncedCount || 0}
                {' '}
                upserted
              </Text>
            ) : null}
            {kountaJob?.status === 'failed' && kountaJob?.error ? (
              <Alert type="error" message={String(kountaJob.error)} showIcon />
            ) : null}
          </PlatformCard>
        </Col>
      </Row>
    </Space>
  );
};

export default CacheOverview;
